<template>
  <label :class="className">
    <VCard v-if="group?.props?.type === 'card'" :clickable="!isDisabled" :ribbon-type>
      <input :id :checked :disabled="isDisabled" type="checkbox" @change="input(($event.target as HTMLInputElement).checked)" />
      <span v-if="!slotContent.component" class="v-checkmark__input" />
      <label class="v-checkbox__label" :for="id">
        <div class="title-20">
          <slot name="title" />
        </div>
        <slot>{{ label }}</slot>
      </label>

      <template v-if="$slots.ribbon" #ribbon>
        <slot name="ribbon" />
      </template>
    </VCard>

    <template v-else>
      <input :id :checked :disabled="isDisabled" type="checkbox" @change="input(($event.target as HTMLInputElement).checked)" />
      <span v-if="!slotContent.component" class="v-checkmark__input" />
      <label class="v-checkbox__label" :for="id">
        <slot>{{ label }}</slot>
      </label>
    </template>
  </label>
</template>

<script setup lang="ts">
import { computed, inject, useId, useSlots } from 'vue'

import { useClassName } from '../composables/useClassName.ts'
import type { VCardRibbonType } from '../types.ts'
import { $checkboxGroupKey, $formKey } from '../utils/injections.ts'
import VCard from './VCard.vue'

interface Props {
  disabled?: boolean | null
  label?: string | number
  ribbonType?: VCardRibbonType
  prop?: string
  indeterminate?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  disabled: null,
  indeterminate: undefined,
  label: '',
  ribbonType: undefined,
  prop: '',
})

const model = defineModel<boolean>({ default: false })

defineSlots<{
  default?(): any
  title?(): any
  ribbon?(): any
}>()

const form = inject($formKey, undefined)
const group = inject($checkboxGroupKey, undefined)
const id = useId()

const isDisabled = computed(() => props.disabled || (props.disabled === null && form?.props?.disabled))
const checked = computed(() => (group ? group.model.value.includes(props.label) : model.value))

const className = useClassName(
  'v-checkbox',
  computed(() => [String(props.label)]),
  computed(() => ({
    'has-label': props.label || slotContent.value.string,
    'is-component': slotContent.value.component,
    'is-disabled': isDisabled.value,
    'is-checked': checked.value,
    'is-indeterminate': props.indeterminate,
  })),
)

const slotContent = computed(() => {
  const content = {
    string: false,
    component: false,
  }

  const slots = useSlots()

  if (!slots.default) {
    return content
  }

  const slot = slots.default()

  if (slot) {
    const slotType = typeof slot[0].type
    content.string = slotType === 'symbol'
    content.component = group?.props?.type !== 'card' && slotType === 'object'
  }

  return content
})

function input(value: boolean) {
  if (isDisabled.value) return

  if (group) {
    group.input(props.label)
  } else {
    model.value = value
  }
}
</script>

<style>
.v-checkbox {
  display: block;
  position: relative;
  cursor: pointer;
  user-select: none;
  line-height: var(--checkbox-size);
  padding-left: 25px;
}

.v-checkbox:not(.has-label) {
  height: var(--checkbox-size);
  width: var(--checkbox-size);
}

.v-checkbox:hover input ~ .v-checkmark__input {
  background-color: var(--color-grey-10);
}

.v-checkbox.is-component {
  padding-left: 0px;
}

.v-checkbox + .v-checkbox {
  margin-top: 5px !important;
}

/* Hide the browser's default checkbox */
.v-checkbox input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
  margin-left: -20px;
}

.v-checkbox .v-card {
  margin-bottom: 10px;
  border-radius: var(--border-radius);
}

.v-checkbox .v-card .v-checkmark__input {
  left: 20px;
  border-color: var(--color-grey-20);
}

.v-checkbox .v-card .v-card__body {
  padding: 15px 20px;
}

.v-checkbox .v-card .v-checkbox__label {
  margin-left: 30px;
  display: flex;
  align-items: initial;
  flex-direction: column;
}

.v-checkbox.is-checked .v-checkmark__input:after {
  display: block;
}

.v-checkbox.is-checked:not(.is-disabled) .v-card {
  border-color: var(--color-grey-80);
}

.v-checkbox .v-checkmark__input {
  position: absolute;
  top: 0;
  left: 0;
  height: var(--checkbox-size);
  width: var(--checkbox-size);
  background-color: #fff;
  border: 1px solid var(--color-grey-20);
  border-radius: 2px;
  transition: background-color var(--transition);
}

.v-checkbox .v-checkmark__input:after {
  content: '';
  position: absolute;
  display: none;
  left: 6px;
  top: 0.75px;
  width: 5px;
  height: 12px;
  border: solid #000;
  border-width: 0 1.5px 1.5px 0;
  transform: rotate(45deg);
}

.v-checkbox .v-checkbox__label {
  cursor: pointer;
}

.v-checkbox.is-disabled {
  cursor: default;
}

.v-checkbox.is-disabled .v-checkmark__input {
  background-color: var(--color-grey-6);
  cursor: default;
}

.v-checkbox.is-disabled .v-checkmark__input:after {
  border-color: var(--color-grey-40);
}

.v-checkbox.is-disabled .v-checkbox__label {
  cursor: default;
}

.v-checkbox.is-indeterminate .v-checkmark__input:after {
  border-width: 0 1.5px 0 0;
  transform: rotate(90deg);
}

.v-checkbox-group--card .v-checkbox .v-checkmark__input,
.v-checkbox-group--horizontal .v-checkbox .v-checkmark__input {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition:
    background-color,
    border-color,
    opacity var(--transition);
}

.v-checkbox-group--card .v-checkbox {
  display: block;
  padding-left: 0;
}

.v-checkbox-group--card .v-checkbox:not(:last-child) {
  margin-right: 0;
}

.v-checkbox-group--horizontal .v-checkbox {
  display: inline-block;
}

.v-checkbox-group--horizontal .v-checkbox:not(:last-child) {
  margin-right: 15px;
}
</style>
